// TabsContainerInforme.jsx mis à jour
import React, { useState, useContext, useEffect } from 'react';
import { Box, Tabs, Tab, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import {
  Description as DescriptionIcon,
  Psychology as PsychologyIcon,
  Straighten as StraightenIcon,
  LocalHospital as LocalHospitalIcon,
  SportsBasketball as SportsBasketballIcon,
  VideoLibrary as VideoLibraryIcon,
  Newspaper as NewspaperIcon,
  FolderOpen as FolderOpenIcon
} from '@mui/icons-material';

import TabsContainerInformeContext from './TabsContainerInformeContext';

// Import des composants
import RechercheInforme from '../screens/RechercheInforme';
import RechercheIntel from '../screens/RechercheIntel';
import AllMedidasTable from './AllMedidasTable';
import AllMedicalTable from './AllMedicalTable';
import AllTechniquesTable from './AllTechniquesTable';
import VideoGallery from './VideoGallery';
import News from '../screens/News';
import AllDocumentsTable from './AllDocumentsTable';

// Styled components 
const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
  },
}));

const StyledTab = styled(Tab)(({ theme, isMobile }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: isMobile ? '0.875rem' : '1rem',
  marginRight: isMobile ? 0 : theme.spacing(1),
  color: theme.palette.text.secondary,
  padding: isMobile ? '6px 12px' : '12px 16px',
  minWidth: isMobile ? 'auto' : 90,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    opacity: 1,
  },
  minHeight: isMobile ? 56 : 48,
  flexDirection: isMobile ? 'column' : 'row',
  '& .MuiTab-iconWrapper': {
    marginBottom: isMobile ? '0px' : '4px',
    marginRight: isMobile ? '0px' : '8px',
  },
  '& .MuiSvgIcon-root': {
    fontSize: isMobile ? '1.25rem' : '1.5rem',
  },
}));

const TabPanel = ({ children, value, index }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`informe-tabpanel-${index}`}
      aria-labelledby={`informe-tab-${index}`}
      sx={{ p: 0 }}
    >
      {value === index && children}
    </Box>
  );
};

const TabsContainerInforme = () => {
  const { t } = useTranslation();
  const { activeTab, setActiveTab } = useContext(TabsContainerInformeContext);
  const [value, setValue] = useState(activeTab);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  // Synchroniser avec le contexte au montage et quand activeTab change
  useEffect(() => {
    setValue(activeTab);
  }, [activeTab]);

  // Gérer l'onglet actif basé sur le state de la navigation
  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      const newTab = location.state.activeTab;
      setValue(newTab);
      setActiveTab(newTab);
    }
  }, [location.state, setActiveTab]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setActiveTab(newValue); // Sauvegarder dans le contexte
  };

  const tabs = [
    { label: t('informeTabs.reports'), component: <RechercheInforme />, icon: <DescriptionIcon /> },
    { label: t('informeTabs.intel'), component: <RechercheIntel />, icon: <PsychologyIcon /> },
    { label: t('informeTabs.measures'), component: <AllMedidasTable />, icon: <StraightenIcon /> },
    { label: t('informeTabs.medical'), component: <AllMedicalTable />, icon: <LocalHospitalIcon /> },
    { label: t('informeTabs.technicalSheets'), component: <AllTechniquesTable />, icon: <SportsBasketballIcon /> },
    { label: t('informeTabs.videos'), component: <VideoGallery />, icon: <VideoLibraryIcon /> },
    { label: t('informeTabs.documents'), component: <AllDocumentsTable />, icon: <FolderOpenIcon /> },
    { label: t('informeTabs.news'), component: <News />, icon: <NewspaperIcon /> },
  ];

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label={t('informeTabs.ariaLabel')}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((tab, index) => (
            <StyledTab
              key={index}
              label={isMobile ? '' : tab.label}
              icon={tab.icon}
              iconPosition={isMobile ? "top" : "start"}
              id={`informe-tab-${index}`}
              aria-controls={`informe-tabpanel-${index}`}
              aria-label={tab.label}
              isMobile={isMobile}
            />
          ))}
        </StyledTabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {tabs.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {tab.component}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default TabsContainerInforme;